import React, { useMemo, useState } from 'react';
import { ShieldAlert, ExternalLink, Info, Sparkles } from 'lucide-react';
import { WorkflowNodeData, NodeErrorHandling, ErrorRule } from '../../../../types';
import { useEditorConfig } from '../../../../contexts/EditorContext';
import { getNodeRules, getNodeRef, summarizeRule } from '../../../../utils/errorRuleset';
import { ERROR_RULESET_PRESETS, getPreset } from '../../../../constants/errorRulesetPresets';
import { RuleEditor } from './RuleEditor';
import { TestPanel } from './TestPanel';
import { INPUT_CLASS } from './uiConstants';

interface ErrorHandlingTabProps {
  data: WorkflowNodeData;
  onChange: (key: string, value: unknown) => void;
}

export const ErrorHandlingTab: React.FC<ErrorHandlingTabProps> = ({ data, onChange }) => {
  const { errorRulesets } = useEditorConfig();
  const lib = errorRulesets;
  const projectRuleSets = lib?.list ?? [];

  const eh = data.errorHandling as NodeErrorHandling | undefined;
  const ref = getNodeRef(eh);
  const boundRuleSet = ref ? projectRuleSets.find(r => r.id === ref) : undefined;

  // Resolved rules: a bound node prefers the live library entry (so library
  // edits show immediately); otherwise its own inlined rules.
  const resolvedRules: ErrorRule[] = boundRuleSet ? boundRuleSet.rules : getNodeRules(eh);

  const [editing, setEditing] = useState(false);

  const selectValue = eh === undefined ? 'default' : ref ? `ref:${ref}` : 'inline';

  const setNodeEH = (next: NodeErrorHandling | undefined) => onChange('errorHandling', next);

  const handlePick = (value: string) => {
    if (value === 'default') { setNodeEH(undefined); setEditing(false); return; }
    if (value === 'inline') { setNodeEH({ rules: resolvedRules.length ? resolvedRules : [] }); setEditing(true); return; }
    if (value === 'new') {
      if (!lib) { setNodeEH({ rules: [] }); setEditing(true); return; }
      const created = lib.create({ name: `Ruleset ${projectRuleSets.length + 1}`, rules: [], source: 'custom' });
      setNodeEH({ $ref: created.id, rules: [] });
      setEditing(true);
      return;
    }
    if (value.startsWith('preset:')) {
      const preset = getPreset(value.slice('preset:'.length));
      if (!preset) return;
      const rules = JSON.parse(JSON.stringify(preset.rules)) as ErrorRule[];
      if (lib) {
        // Instantiate an editable project ruleset seeded from the preset.
        const created = lib.create({ name: preset.name, rules, source: `from ${preset.name}` });
        setNodeEH({ $ref: created.id, rules });
      } else {
        setNodeEH({ rules });
      }
      setEditing(false);
      return;
    }
    if (value.startsWith('ref:')) {
      const id = value.slice('ref:'.length);
      const rs = projectRuleSets.find(r => r.id === id);
      setNodeEH({ $ref: id, rules: rs ? rs.rules : [] });
      setEditing(false);
      return;
    }
  };

  // Editing writes to the library entry (bound) or the node (inline).
  const handleRulesChange = (rules: ErrorRule[]) => {
    if (ref) {
      lib?.update(ref, { rules });
      setNodeEH({ $ref: ref, rules }); // keep node's inlined copy in sync
    } else {
      setNodeEH({ rules });
    }
  };

  const usage = ref && lib ? lib.usageCount(ref) : 0;
  const hasLegacy = (data.onException !== undefined || data.retries !== undefined);
  const showConvertBanner = hasLegacy && eh === undefined;

  const editorHeading = useMemo(() => (
    <div className="flex items-center gap-2">
      <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
        {boundRuleSet ? `«${boundRuleSet.name}»` : 'Inline ruleset (this task only)'}
      </span>
      {ref && usage > 0 && <span className="text-[11px] text-slate-400">used by {usage} node{usage === 1 ? '' : 's'}</span>}
    </div>
  ), [boundRuleSet, ref, usage]);

  return (
    <div className="p-6 space-y-5 max-w-4xl">
      <div className="flex items-center gap-2">
        <ShieldAlert size={18} className="text-teal-600" />
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Error handling for this task</h3>
      </div>

      {showConvertBanner && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20 p-3 flex gap-3">
          <Info size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Legacy <span className="font-mono">On-Exception</span> is ignored by the engine; this task uses default rules.
              Convert to assign a ruleset.
            </p>
            <button type="button" onClick={() => handlePick('new')}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md bg-amber-600 text-white hover:bg-amber-700">
              <Sparkles size={12} /> Convert
            </button>
          </div>
        </div>
      )}

      {/* picker */}
      <div className="space-y-1.5">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ruleset</label>
        <select value={selectValue} onChange={(e) => handlePick(e.target.value)} className={INPUT_CLASS}>
          <option value="default">Default (none — engine defaults)</option>
          <option value="inline">Inline (custom for this task only)</option>
          <optgroup label="Presets">
            {ERROR_RULESET_PRESETS.map(p => <option key={p.key} value={`preset:${p.key}`}>{p.name}</option>)}
          </optgroup>
          {projectRuleSets.length > 0 && (
            <optgroup label="Project rulesets">
              {projectRuleSets.map(r => (
                <option key={r.id} value={`ref:${r.id}`}>{r.name}{lib ? ` (used ${lib.usageCount(r.id)}×)` : ''}</option>
              ))}
            </optgroup>
          )}
          <option value="new">+ New ruleset…</option>
        </select>
      </div>

      {/* preview / editor */}
      {eh !== undefined && (
        editing ? (
          <div className="rounded-xl border border-teal-200 dark:border-teal-900 p-4">
            <RuleEditor rules={resolvedRules} onChange={handleRulesChange} heading={editorHeading} />
            <div className="mt-3 flex justify-end">
              <button type="button" onClick={() => setEditing(false)}
                className="px-3 py-1.5 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg">Done</button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-800">
              <div className="text-sm font-bold text-slate-700 dark:text-slate-200">
                Preview: {boundRuleSet ? `«${boundRuleSet.name}»` : 'inline'}
                {ref && usage > 0 && <span className="ml-2 text-[11px] font-normal text-slate-400">used by {usage} node{usage === 1 ? '' : 's'}</span>}
              </div>
              <button type="button" onClick={() => setEditing(true)}
                className="flex items-center gap-1 text-xs font-medium text-teal-600 hover:underline">
                Edit ruleset <ExternalLink size={12} />
              </button>
            </div>
            <div className="p-3 space-y-1">
              {resolvedRules.length === 0 && <p className="text-xs text-slate-400">No rules yet — click Edit to add some.</p>}
              {resolvedRules.map((r, i) => {
                const s = summarizeRule(r);
                return (
                  <div key={i} className="text-xs text-slate-600 dark:text-slate-300 font-mono">
                    <span className="text-slate-400">WHEN</span> {s.when} <span className="text-slate-400">→</span> {s.then}
                  </div>
                );
              })}
              <div className="text-[11px] text-slate-400 pt-1">(defaults) transient→retry ∞ · else→throw "{'{surfaced}'}"</div>
            </div>
          </div>
        )
      )}

      {eh !== undefined && resolvedRules.length > 0 && <TestPanel rules={resolvedRules} />}

      {ref && usage > 1 && (
        <p className="text-[11px] text-slate-400 flex items-center gap-1">
          <Info size={12} /> Editing this ruleset updates all {usage} nodes that use it.
        </p>
      )}
    </div>
  );
};

export default ErrorHandlingTab;
